import { useCallback, useEffect, useState, useRef } from 'react'
import s from './styles.module.scss'
import * as api from 'api'
import { Alert, Layouts, Loader } from 'components'
import { head, getTimeFormats } from 'helpers'

const PER_PAGE = 20

export default function ReferralTracking() {
  const [referrers, setReferrers] = useState([])
  const [loading, setLoading] = useState(false)
  const [pageNo, setPageNo] = useState(1)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedReferrer, setExpandedReferrer] = useState(null)
  const searchTimeoutRef = useRef(null)

  const fetchReferrals = useCallback(async () => {
    setLoading(true)
    try {
      const response = await api.admin.analytics.fetchReferrals({
        pageNo,
        perPage: PER_PAGE,
        searchQuery: searchQuery.trim() || undefined
      })

      if (response?.code === 200) {
        setReferrers(response.payload.referrers || [])
        setTotal(response.payload.total || 0)
        setTotalPages(response.payload.totalPages || 0)
      } else if (response?.message) {
        Alert.error(response.message)
      }
    } catch (error) {
      console.error('Failed to load referrals', error)
      Alert.error('Failed to load referral data')
    } finally {
      setLoading(false)
    }
  }, [pageNo, searchQuery])

  useEffect(() => {
    head({ title: 'Referral Tracking | Admin Dashboard | BCN' })
    fetchReferrals()
  }, [fetchReferrals])

  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchQuery(value)
    setPageNo(1) // Reset to first page on search

    // Debounce search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    searchTimeoutRef.current = setTimeout(() => {
      fetchReferrals()
    }, 500)
  }

  const toggleExpand = (referrerId) => {
    setExpandedReferrer(expandedReferrer === referrerId ? null : referrerId)
  }

  return (
    <div className={s.main}>
      <Layouts.Classic title='Referral Tracking' />
      <div className={s.content}>
        <div className={s.header}>
          <div className={s.searchSection}>
            <input
              type='text'
              placeholder='Search by name, user ID, phone, or email...'
              value={searchQuery}
              onChange={handleSearchChange}
              className={s.searchInput}
            />
          </div>
          <div className={s.stats}>
            <div className={s.statCard}>
              <div className={s.statLabel}>Total Referrers</div>
              <div className={s.statValue}>{total}</div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className={s.loader}>
            <Loader color='var(--c-primary)' colorText='var(--c-primary)' />
          </div>
        ) : referrers.length === 0 ? (
          <div className={s.emptyState}>
            {searchQuery ? 'No referrers found matching your search.' : 'No referrers found.'}
          </div>
        ) : (
          <>
            <div className={s.referrersList}>
              {referrers.map((item, index) => (
                <div key={item.referrer._id || index} className={s.referrerCard}>
                  <div className={s.referrerHeader} onClick={() => toggleExpand(item.referrer._id)}>
                    <div className={s.referrerInfo}>
                      <div className={s.referrerName}>
                        {item.referrer.name || 'Unknown User'}
                        {item.totalReferred > 0 && (
                          <span className={s.badge}>{item.totalReferred} referred</span>
                        )}
                      </div>
                      <div className={s.referrerDetails}>
                        <span>ID: {item.referrer.userRefId}</span>
                        {item.referrer.phoneNo && <span>Phone: {item.referrer.phoneNo}</span>}
                        {item.referrer.email && <span>Email: {item.referrer.email}</span>}
                        <span>{item.referrer.city}, {item.referrer.state}</span>
                        <span>Plan: {item.referrer.plan}</span>
                      </div>
                    </div>
                    <div className={s.referrerActions}>
                      <span className={s.count}>{item.totalReferred} users</span>
                      <span className={`material-icons ${s.expandIcon} ${expandedReferrer === item.referrer._id ? s.expanded : ''}`}>
                        {expandedReferrer === item.referrer._id ? 'expand_less' : 'expand_more'}
                      </span>
                    </div>
                  </div>

                  {expandedReferrer === item.referrer._id && item.referredUsers.length > 0 && (
                    <div className={s.referredUsersList}>
                      <div className={s.referredUsersHeader}>
                        Users referred by {item.referrer.name} ({item.totalReferred})
                      </div>
                      <div className={s.referredUsersTable}>
                        <div className={s.tableHeader}>
                          <div>Name</div>
                          <div>User ID</div>
                          <div>Phone</div>
                          <div>Email</div>
                          <div>Location</div>
                          <div>Plan</div>
                          <div>Joined</div>
                        </div>
                        {item.referredUsers.map((user, userIndex) => (
                          <div key={user._id || userIndex} className={s.tableRow}>
                            <div className={s.userName} data-label='Name'>
                              {user.firstName} {user.lastName}
                            </div>
                            <div className={s.userId} data-label='User ID'>{user.userRefId}</div>
                            <div data-label='Phone'>{user.phoneNo || '—'}</div>
                            <div data-label='Email'>{user.email || '—'}</div>
                            <div data-label='Location'>
                              {user.city || '—'}, {user.state || '—'}
                            </div>
                            <div data-label='Plan'>{user.plan || 'Plan 0'}</div>
                            <div data-label='Joined'>{user.createdAt ? getTimeFormats(new Date(user.createdAt)) : '—'}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {expandedReferrer === item.referrer._id && item.referredUsers.length === 0 && (
                    <div className={s.noReferredUsers}>
                      No users have been referred by this user yet.
                    </div>
                  )}
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className={s.pagination}>
                <button
                  onClick={() => setPageNo(prev => Math.max(1, prev - 1))}
                  disabled={pageNo === 1}
                  className={s.pageButton}
                >
                  Previous
                </button>
                <span className={s.pageInfo}>
                  Page {pageNo} of {totalPages} ({total} total)
                </span>
                <button
                  onClick={() => setPageNo(prev => Math.min(totalPages, prev + 1))}
                  disabled={pageNo === totalPages}
                  className={s.pageButton}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

